/**
 * Per-event guest RSVP assignments (guest_events) — the normalized table
 * the guest portal reads (kalligo-api's getInvitedEventIds/
 * getDetailAccessMap). See kalligo-api's routes/planning/events.ts and
 * routes/planning/guests.ts's :ref/events route.
 */

import { planningPath, apiGet } from './PlanningApiClient';
import { sdk } from './guestSdk';
import { RSVPStatus } from '../types/Guests';

export type InvitedBy = 'bride' | 'groom';

export interface GuestEventAssignment {
  guest_id: string;
  guest_ref: string;
  guest_name: string;
  event_id: string;
  rsvp_status: RSVPStatus;
  // Deprecated, read-only: derived by the API from the sponsor. Write invited_by_sponsor_id instead.
  invited_by: InvitedBy | null;
  invited_by_sponsor_id: string | null;
  created_at: string;
  updated_at: string;
}

export function loadGuestEventAssignments(guestRef: string): Promise<GuestEventAssignment[]> {
  return apiGet<{ events: GuestEventAssignment[] }>(planningPath(`/guests/${guestRef}/events`)).then((r) => r.events);
}

/**
 * Assign-or-update through GraphQL: update the answer when the guest is already invited to the event, otherwise
 * invite them with this answer. (Choosing WHO invited a guest to an event arrives with the Events module.)
 */
export async function setGuestEventRsvp(eventId: string, guestRef: string, rsvpStatus: RSVPStatus): Promise<void> {
  const updated = await sdk.UpdateGuestEventRsvp({ event_id: eventId, guest_ref: guestRef, rsvp_status: rsvpStatus });
  if (updated.updateGuestEventRsvp) return;
  await sdk.AssignGuestToEvent({ event_id: eventId, guest_ref: guestRef, rsvp_status: rsvpStatus });
}

/** Un-invites a guest from an event entirely (the invitation row is deleted, not just changed). */
export async function removeGuestFromEvent(eventId: string, guestRef: string): Promise<void> {
  await sdk.RemoveGuestFromEvent({ event_id: eventId, guest_ref: guestRef });
}

// ── per-event answers as the guest screens use them ──

/** One invitation of one guest, with the event's title (GET /guests/:ref/events). */
export interface GuestEventInvitation {
  event_id: string;
  event_title: string;
  rsvp_status: RSVPStatus;
  invited_by: InvitedBy | null;
}

export function loadGuestInvitations(guestRef: string): Promise<GuestEventInvitation[]> {
  return apiGet<{ events: GuestEventInvitation[] }>(planningPath(`/guests/${encodeURIComponent(guestRef)}/events`)).then(r => r.events ?? []);
}

/** Every guest's answer for every event, in one read (the list's RSVP column). */
export interface ProjectRsvp { guest_ref: string; event_id: string; event_title: string; rsvp_status: RSVPStatus }

export function loadProjectRsvps(): Promise<ProjectRsvp[]> {
  return apiGet<{ rsvps: ProjectRsvp[] }>(planningPath('/guests/event-rsvps')).then(r => r.rsvps ?? []);
}

/** The project-wide read with each live guest's version stamp (ISO, milliseconds): what the bulk RSVP save compares against. */
export interface ProjectRsvpState { rsvps: ProjectRsvp[]; guests: Array<{ guest_ref: string; updated_at: string }> }

export function loadProjectRsvpState(): Promise<ProjectRsvpState> {
  return apiGet<{ rsvps?: ProjectRsvp[]; guests?: ProjectRsvpState['guests'] }>(planningPath('/guests/event-rsvps'))
    .then(r => ({ rsvps: r.rsvps ?? [], guests: r.guests ?? [] }));
}
